import React from "react";
import { fmtCurrency } from "../../shared/format.js";

/**
 * HeroNumber — shared evidence component for weather_loss and escape_pressure.
 *
 * Renders a large hero figure with supporting stats beneath.
 * Adapts layout based on which panel key is present.
 *
 * Spec references: §7 (weather_loss, escape_pressure).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const HeroNumber = ({ panel }) => {
  const f = panel.facts || {};

  // ─── weather_loss ──────────────────────────────────────────────────────────
  if (panel.panel === "weather_loss") {
    const pct = Number(f.loss_as_pct_of_revenue) || 0;
    const revenueLost = Number(f.revenue_lost_to_conditions) || 0;

    return (
      <div>
        {/* Two stats side by side — compact, not full width */}
        <div className="flex items-baseline justify-center gap-6">
          <div className="text-right">
            <span className="text-4xl font-bold text-orange-500 leading-none">
              {pct}%
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              of revenue lost to weather
            </p>
          </div>
          <div className="w-px h-12 bg-gray-200 dark:bg-gray-700 self-center" />
          <div className="text-left">
            <span className="text-4xl font-bold text-gray-700 dark:text-gray-300 leading-none">
              {fmtCurrency(revenueLost, { dropCents: true })}
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              max possible loss
            </p>
          </div>
        </div>

        {/* FYI-style muted caption */}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 text-center italic">
          Theoretical maximum if every day had perfect weather. Not recoverable revenue.
        </p>
      </div>
    );
  }

  // ─── escape_pressure ───────────────────────────────────────────────────────
  if (panel.panel === "escape_pressure") {
    const current = Number(f.current_7d_f) || 0;
    const baseline = Number(f.baseline_90d_f) || 0;
    const change = Number(f.change_vs_baseline_f) || 0;
    const sessions = Number(f.sessions_current_7d) || 0;

    const abs = Math.abs(current);

    // Sign-aware wording — never say "−0.5° warmer"
    const magnitudeText =
      current === 0
        ? "about the same temperature as"
        : `${abs}°F ${current > 0 ? "warmer" : "cooler"} than`;

    const changeColor =
      change > 0
        ? "text-emerald-600 dark:text-emerald-400"
        : change < 0
          ? "text-blue-500"
          : "text-gray-500";

    return (
      <div className="space-y-3">
        {/* Hero figure — signed number */}
        <div className="flex items-baseline gap-2">
          <span
            className={`text-4xl font-bold leading-none ${
              current > 0
                ? "text-orange-500"
                : current < 0
                  ? "text-blue-500"
                  : "text-gray-500"
            }`}
          >
            {current > 0 ? "+" : ""}
            {current}°F
          </span>
        </div>

        {/* Baseline + change */}
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            90-day baseline: {baseline}°F
          </span>
          <span className={`font-semibold ${changeColor}`}>
            ({change > 0 ? "+" : ""}
            {change}°F)
          </span>
        </div>

      </div>
    );
  }

  return null;
};

export default HeroNumber;